import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Bike } from "lucide-react";

/**
 * Phase 3.56 — Google Auth callback landing page.
 *
 * The Emergent OAuth flow returns the browser to ``/auth/callback#session_id=...``.
 * This page picks the fragment up, posts it to the backend in exchange for our
 * app JWT, and then forwards the user to the role-appropriate landing page.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH.
 */
export default function AuthCallbackPage() {
    const navigate = useNavigate();
    const { setSession } = useAuth();
    const hasProcessed = useRef(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (hasProcessed.current) return;
        hasProcessed.current = true;

        const hash = window.location.hash || "";
        const m = hash.match(/session_id=([^&]+)/);
        if (!m) {
            setError("Keine gültige Google-Session erkannt. Bitte erneut anmelden.");
            setTimeout(() => navigate("/login", { replace: true }), 2000);
            return;
        }
        const sessionId = decodeURIComponent(m[1]);
        const acceptedTerms = (() => {
            try { return sessionStorage.getItem("tw_google_accepted_terms") === "1"; }
            catch { return false; }
        })();

        (async () => {
            try {
                const { data } = await api.post("/auth/google/session", {
                    session_id: sessionId,
                    accepted_terms: acceptedTerms,
                });
                if (data?.token) {
                    setSession(data.user, data.token);
                }
                try { sessionStorage.removeItem("tw_google_accepted_terms"); } catch { /* ignore */ }
                const role = data?.user?.role;
                // Clear the fragment so a refresh won't re-trigger the exchange.
                window.history.replaceState({}, "", "/auth/callback");
                if (role === "vermittler") navigate("/vermittler", { replace: true });
                else if (role === "service_provider") navigate("/dienstleister/dashboard", { replace: true });
                else if (role === "admin") navigate("/admin", { replace: true });
                else navigate("/dashboard", { replace: true });
            } catch (e) {
                const detail = e?.response?.data?.detail || "Google-Anmeldung fehlgeschlagen.";
                setError(typeof detail === "string" ? detail : "Google-Anmeldung fehlgeschlagen.");
                setTimeout(() => navigate("/login", { replace: true }), 3000);
            }
        })();
    }, [navigate, setSession]);

    return (
        <div className="min-h-[calc(100vh-200px)] flex items-center justify-center px-4 py-12" data-testid="auth-callback-page">
            <div className="text-center">
                <div className="w-14 h-14 bg-[#FFCC00] flex items-center justify-center rounded-sm mx-auto mb-4">
                    <Bike className="w-8 h-8 text-[#111827]" strokeWidth={2.5} />
                </div>
                {error ? (
                    <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-sm max-w-md" data-testid="auth-callback-error">
                        {error}
                    </div>
                ) : (
                    <>
                        <div className="font-display text-xl font-black mb-1">Anmeldung läuft …</div>
                        <p className="text-sm text-gray-600">Wir richten dein Konto ein.</p>
                        <div className="mt-4 inline-block w-6 h-6 border-2 border-[#111827] border-t-transparent rounded-full animate-spin" />
                    </>
                )}
            </div>
        </div>
    );
}
